import React from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { postClientLog } from '../../utils/clientLog';

interface Props {
  children: React.ReactNode;
  /** Opcional — fallback custom. Se omitido, usa o painel default. */
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Captura erros de render de componentes filhos e mostra fallback em vez de
 * quebrar a página inteira. Plug uma vez em App.tsx envolvendo o `<Outlet />`
 * — caso um sub-componente exploda (erro de prop, undefined.foo, etc), o resto
 * do app continua de pé.
 *
 * NÃO captura erros assíncronos (use try/catch + toast), de event handlers,
 * de SSR (não temos SSR), ou da própria ErrorBoundary (use múltiplas se
 * precisar de isolamento por seção).
 *
 * Em produção, logar no Sentry/similar fica como TODO — hoje só `console.error`.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Componente quebrou:', error, info);
    postClientLog({
      level: 'error',
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      extra: { source: 'boundary' },
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        className="flex min-h-[50vh] items-center justify-center p-4"
      >
        <div className="w-full max-w-md rounded-lg border border-rose-200 bg-rose-50 p-6 dark:border-rose-900 dark:bg-rose-950/30">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-rose-600 dark:text-rose-400" aria-hidden />
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-rose-900 dark:text-rose-100">
                Algo deu errado nesta tela
              </h2>
              <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                O componente travou inesperadamente. Você pode tentar novamente sem perder o resto do app.
              </p>
              {this.state.error && (
                <details className="mt-3 text-[11px] text-rose-700/80 dark:text-rose-300/80">
                  <summary className="cursor-pointer select-none">Detalhes técnicos</summary>
                  <pre className="mt-1 whitespace-pre-wrap break-words font-mono">
                    {this.state.error.message}
                  </pre>
                </details>
              )}
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={this.handleReset}
                  className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden />
                  Tentar de novo
                </button>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300 dark:hover:bg-rose-900/40"
                >
                  Recarregar página
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
